import { Box, Center, Flex, Heading } from '@chakra-ui/layout';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    IconButton,
    Spinner,
    Tooltip,
    useClipboard,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useContext, useEffect, useRef } from 'react';
import { FaCopy, FaTrash } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import config from '../config';
import ProfileContext, {
    getProfileErrorHeader,
    getProfileErrorText,
} from '../context/ProfileContext';
import SelectedGameContext from '../context/SelectedGameContext';
import { Timeout } from '../util/types';

export const Overview: React.FC<{}> = (props) => {
    const selectedGame = useContext(SelectedGameContext);
    // TODO overview should be at subpath '/' not '/overview', or redirect '/' to '/overview'
    const profile = useContext(ProfileContext);
    const currLicense = selectedGame?.currLicense;
    const date = new Date(currLicense?.createdAt);
    const dateCreated = date.toDateString();
    const timeCreated = date.toTimeString();
    const { hasCopied, onCopy } = useClipboard(currLicense?.key);
    const toast = useToast();
    const timeoutRef = useRef<Timeout>();
    const areLicensesLoaded = useRef(false);
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const name = currLicense?.configs?.name || 'Unnamed Game';
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();

    const licenseChecker = () => {
        if (!areLicensesLoaded.current) {
            console.log('Experiencing a slow response time...');
            return toast({
                title: 'Still Loading',
                description: 'Hmm, this is taking longer than expected...',
                status: 'warning',
                duration: 9000,
                variant: 'subtle',
                isClosable: true,
            });
        }
    };

    const deleteGame = async () => {
        const resp = await fetch(`${config.licenseServiceURL}/license/${currLicense?.key}`, {
            headers: {
                Authorization: `Bearer ${keycloak.token}`,
            },
            method: 'DELETE',
        });
        const response = await resp.json();
        // If deletion successful, success Toast notification
        if (resp.status === 200) {
            await keycloak.updateToken(-1);
            navigate(`/`);
            console.log(`'${name}' deleted.`);
            await profile.getLicensesFromToken();
            return toast({
                title: 'Success',
                description: `'${name}' deleted.`,
                status: 'success',
                duration: 9000,
                isClosable: true,
            });
        } else {
            // If deletion unsuccessful, error Toast notification
            console.log('Error deleting game');
            return toast({
                title: 'Error',
                description: `Awkward! Error deleting '${name}'. Please try again later...`,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
    };

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {profile.errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {profile.errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    useEffect(() => {
        areLicensesLoaded.current = profile?.licensesLoaded;
    }, [profile]);

    useEffect(() => {
        timeoutRef.current = setTimeout(licenseChecker, 9000);
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (hasCopied) {
            toast({
                description: 'License ID copied to clipboard',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
        }
    }, [hasCopied]);

    const displaySection = () => {
        return (
            <>
                <Flex display={'flex'}>
                    <Heading>{name}</Heading>
                    <Box flex="1"></Box>
                </Flex>
                <Box>
                    <Heading as="h4" size="md">
                        License ID
                    </Heading>
                    <Flex alignItems="center">
                        {currLicense?.key}
                        <Tooltip label="Copy to Clipboard">
                            <IconButton
                                size="md"
                                variant="ghost"
                                onClick={onCopy}
                                marginLeft={'1em'}
                                aria-label="Copy License ID to Clipboard"
                                icon={<FaCopy />}
                            />
                        </Tooltip>
                    </Flex>
                    <Heading as="h5" size="s">
                        Created:
                    </Heading>
                    <p>Date: {dateCreated}</p>
                    <p>Time: {timeCreated}</p>
                    <Link to={`/game/${currLicense?.key}/Owners`}>
                        <Button marginTop={'3em'} marginRight={'3em'}>
                            List Of Owners
                        </Button>
                    </Link>
                    <Link to={`/game/${currLicense?.key}/Leaderboards`}>
                        <Button marginTop={'3em'} marginRight={'3em'}>
                            Leaderboards
                        </Button>
                    </Link>
                    <Button colorScheme="red" onClick={onDeleteOpen} marginTop={'3em'}>
                        Delete game?
                    </Button>
                </Box>
            </>
        );
    };

    return (
        <Box>
            {currLicense ? (
                displaySection()
            ) : profile.errors.length > 0 ? (
                <>
                    <Heading textAlign={'center'} marginBottom={'1em'}>
                        Oh No! Error Detected
                    </Heading>
                    {renderError()}
                </>
            ) : (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            )}
            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelRef}
                onClose={onDeleteClose}
                isCentered
                motionPreset="slideInBottom"
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete '{name}'?
                        </AlertDialogHeader>
                        <AlertDialogBody>Are you sure you want to delete '{name}'?</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={() => {
                                    deleteGame();
                                    onDeleteClose();
                                }}
                                ml={3}
                            >
                                <FaTrash style={{ marginRight: '1em' }} /> Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};
